/* Campaign only: native dialog + the theme's server-rendered Shopify contact form. */
(() => {
  if (customElements.get('art-exhibition')) return;

  class ArtExhibition extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.enquiry = this.querySelector('[data-art-enquiry]');
      this.enquireButton = this.querySelector('[data-art-enquire]');
      this.storageKey = `art-exhibition:${location.pathname}:${this.dataset.sectionId}`;
      this.querySelectorAll('[data-art-open]').forEach(button => { button.disabled = false; });
      this.addEventListener('click', event => {
        const open = event.target.closest('[data-art-open]');
        if (open) this.openWork(open.dataset.artOpen, false, open);
        if (event.target.closest('[data-art-close]')) this.dialog.close();
        if (event.target.closest('[data-art-enquire]')) this.toggleEnquiry(true);
        if (event.target.closest('[data-art-enquiry-close]')) this.toggleEnquiry(false);
      }, options);
      // A backdrop click targets the dialog itself; clicks inside its bounds do not close it.
      this.dialog.addEventListener('click', event => {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.release(), options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(this.dialog.querySelectorAll('button, input:not([type="hidden"]), textarea, select, a[href], [tabindex="0"]'))
          .filter(control => !control.disabled && control.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);
      this.form.addEventListener('submit', event => {
        if (this.currentSold) {
          event.preventDefault();
          return;
        }
        // Do not intercept the POST: Shopify retains its validation and spam protection.
        // Persist only artwork identity and browsing position, never customer form values.
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify({ work: this.currentWork, scroll: this.scrollPosition, time: Date.now() }));
        } catch (_) { /* The return URL also carries the artwork when storage is unavailable. */ }
        const returnInput = this.form.querySelector('[name="return_to"]');
        if (returnInput) {
          const returnURL = new URL(location.href);
          returnURL.searchParams.set('contact_posted', 'true');
          returnURL.hash = `art-enquiry-${encodeURIComponent(this.currentWork)}`;
          returnInput.value = returnURL.pathname + returnURL.search + returnURL.hash;
        }
      }, options);
      this.addEventListener('shopify:block:select', event => this.openWork(event.detail.blockId), options);
      this.restoreResult();
    }

    openWork(id, restore = false, opener = null) {
      const template = Array.from(this.querySelectorAll('[data-art-template]')).find(item => item.dataset.artTemplate === id);
      if (!template) return;
      this.opener = opener || Array.from(this.querySelectorAll('[data-art-open]')).find(item => item.dataset.artOpen === id);
      if (!restore && this.currentWork !== id) this.form.querySelector('[name="contact[body]"]').value = `I would like to enquire about ${template.dataset.title}.`;
      this.currentWork = id;
      this.currentSold = template.dataset.sold === 'true';
      // Preserve a returned Shopify confirmation if a work sold after an enquiry was sent.
      this.soldResult = this.currentSold && restore && !!this.querySelector('[data-art-result]');
      const enquiryArea = this.querySelector('[data-art-enquiry-area]');
      const enquiryNote = this.querySelector('[data-art-enquiry-note]');
      if (enquiryArea) enquiryArea.hidden = this.currentSold && !this.soldResult;
      if (enquiryNote) enquiryNote.hidden = this.currentSold;
      this.enquireButton.hidden = this.currentSold;
      this.enquireButton.disabled = this.currentSold;
      const content = template.content.cloneNode(true);
      const afterContent = content.querySelector('[data-art-after-content]');
      const afterHost = this.querySelector('[data-art-after]');
      // Keep one server-rendered form; only the artwork content is replaced.
      if (afterHost) {
        afterHost.replaceChildren();
        if (afterContent) {
          afterHost.append(...Array.from(afterContent.children));
          afterContent.remove();
        }
        afterHost.hidden = !afterHost.children.length;
        this.dialog.classList.toggle('ArtExhibition__drawer--has-after', !afterHost.hidden);
      }
      this.querySelector('[data-art-detail]').replaceChildren(content);
      this.querySelector('[data-art-enquiry-title]').textContent = `Enquire about ${template.dataset.title}`;
      this.querySelector('[data-art-reference]').value = `Art Enquiry — ${template.dataset.title}`;
      this.querySelector('[data-art-product-id]').value = template.dataset.productId;
      this.querySelector('[data-art-product-handle]').value = template.dataset.productHandle;
      this.querySelectorAll('[data-art-result]').forEach(result => { result.hidden = !restore; });
      this.querySelector('[data-art-fields]').hidden = this.currentSold || (restore && !!this.querySelector('[data-art-result="success"]'));
      this.toggleEnquiry(false, false);
      if (!this.dialog.open) {
        this.scrollPosition = window.scrollY;
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
      }
      this.dialog.scrollTop = 0;
      this.querySelector('[data-art-close]').focus({ preventScroll: true });
    }

    toggleEnquiry(open, focus = true) {
      if (open && this.currentSold && !this.soldResult) return;
      this.enquiry.hidden = !open;
      this.enquireButton.setAttribute('aria-expanded', String(open));
      if (!focus) return;
      if (open) {
        const target = this.enquiry.querySelector('[data-art-result]:not([hidden])') || this.enquiry.querySelector('input:not([type="hidden"])');
        target?.focus({ preventScroll: true });
        this.enquiry.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      } else (this.currentSold ? this.querySelector('[data-art-close]') : this.enquireButton).focus({ preventScroll: true });
    }

    restoreResult() {
      let pending;
      try { pending = JSON.parse(sessionStorage.getItem(this.storageKey)); } catch (_) { /* Storage is optional. */ }
      if (pending && Date.now() - pending.time > 30 * 60 * 1000) pending = null;
      const hashWork = location.hash.startsWith('#art-enquiry-') ? decodeURIComponent(location.hash.slice(13)) : null;
      const hasResult = this.querySelector('[data-art-result]');
      if (!hasResult || !(pending?.work || hashWork)) return;
      if (pending) window.scrollTo({ top: pending.scroll, behavior: 'instant' });
      this.openWork(pending?.work || hashWork, true);
      if (!this.dialog.open) return;
      if (pending) this.scrollPosition = pending.scroll;
      this.toggleEnquiry(true);
      try { sessionStorage.removeItem(this.storageKey); } catch (_) { /* Storage is optional. */ }
      // Keep the confirmation in the DOM, but avoid replaying it on the next refresh.
      const cleanURL = new URL(location.href);
      cleanURL.searchParams.delete('contact_posted');
      cleanURL.hash = '';
      history.replaceState(history.state, '', cleanURL);
    }

    release() {
      if (this.previousOverflow === undefined) return;
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
      window.scrollTo({ top: this.scrollPosition, behavior: 'instant' });
      this.opener?.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.release();
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('art-exhibition', ArtExhibition);
})();